<template>
    <div
        class="apprentice-simple-card personal-goals"
        v-if="personalGoals.length !== 0">
        <div class="component-title">
            <h5 class="title">Gearchiveerde leerdoelen</h5>
        </div>

        <div
            class="not-found with-header empty-list"
            v-if="personalGoals && personalGoals.length === 0">
            <div
                class="btn-add-circle icon"
                v-b-tooltip.hover
                v-if="!teacherAccess"
                :title="$t('common.add_learning_goal')"
                @click="addPersonalGoal"></div>
            <div
                class="alert-notice"
                v-if="!teacherAccess">
                {{ $t('personal_goals.you_do_not_have_a_learning_goal_yet') }}
            </div>
            <div
                class="alert-notice"
                v-if="teacherAccess">
                {{ $t('personal_goals.no_learning_goals_have_been_set') }}
            </div>
        </div>

        <div
            class="personal-goal-accordion"
            v-for="goal in personalGoals"
            v-else
            role="tablist"
            :key="goal.id">
            <div class="personal-goal">
                <div
                    class="personal-goal-header"
                    role="tab">
                    <div
                        class="personal-goal-content"
                        v-b-toggle="`acc-personal-goal-${goal.id}`">
                        <design-2021-personal-goal-widget-radial-bar-chart-component :personal-goal-in="goal">
                        </design-2021-personal-goal-widget-radial-bar-chart-component>

                        <div class="personal-goal-title">
                            {{ goal.name }}
                            <div class="personal-goal-task-notice muted-text">
                                {{ getPersonalGoalTasksStatus(goal) }}
                            </div>
                        </div>
                        <b-dropdown
                            class="ellipsis-nav personal-goal-nav"
                            v-if="!teacherAccess"
                            variant="primary"
                            right
                            text="Right align"
                            no-caret
                            toggle-class="personal-goal-context-menu">
                            <template #button-content>
                                <i class="fas fa-ellipsis-v"></i>
                            </template>
                            <b-dropdown-item
                                class="btn-add-task"
                                @click.prevent.stop="addPersonalGoalTask(goal)">
                                <i class="btn-add"></i>
                                {{ $t('personal_goals.add_task') }}
                            </b-dropdown-item>
                            <b-dropdown-item
                                class="btn-edit-personal-goal"
                                @click.prevent.stop="showUpdatePersonalGoalModal(goal)">
                                <i class="btn-edit"></i>{{ $t('common.edit_learning_goal') }}
                            </b-dropdown-item>
                            <b-dropdown-item
                                class="btn-archive-personal-goal"
                                @click.prevent.stop="unarchivePersonalGoal(goal)">
                                <i class="btn-box"></i>{{ $t('personal_goals.undo_archiving') }}
                            </b-dropdown-item>
                            <b-dropdown-item
                                class="btn-remove-personal-goal"
                                @click.prevent.stop="removePersonalGoal(goal)">
                                <i class="btn-remove"></i>{{ $t('common.delete_learning_goal') }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                </div>
                <b-collapse
                    class="task-list"
                    v-if="goal.tasks.length > 0"
                    :id="`acc-personal-goal-${goal.id}`"
                    accordion="accordion-personal-goal"
                    role="tabpanel">
                    <design-2021-personal-goal-widget-task-component
                        v-for="task in goal.tasks"
                        :key="task.id"
                        :task-in="task"
                        :personal-goal-id="goal.id"
                        :teacher-access="teacherAccess">
                    </design-2021-personal-goal-widget-task-component>
                    <div
                        class="add-task"
                        v-if="!teacherAccess">
                        <b-button
                            class="btn-add-circle"
                            variant="outline-primary"
                            @click="addPersonalGoalTask(goal)"
                            >Taak</b-button
                        >
                    </div>
                </b-collapse>
                <b-collapse
                    class="task-list"
                    v-else
                    :id="`acc-personal-goal-${goal.id}`"
                    accordion="accordion-personal-goal"
                    role="tabpanel">
                    <div
                        class="not-found with-header empty-personal-goal-list"
                        v-b-tooltip.hover
                        :title="$t('personal_goals.add_task')"
                        @click="showUpdatePersonalGoalModal(goal)">
                        <div class="btn-add-circle icon"></div>
                        <div class="alert-notice">{{ $t('personal_goals.this_learning_goal_has_no_tasks') }}</div>
                    </div>
                </b-collapse>
            </div>
        </div>
        <design-2021-personal-goal-widget-task-update-modal-component ref="personal-goal-update-modal">
        </design-2021-personal-goal-widget-task-update-modal-component>

        <design-2021-personal-goal-widget-add-task-modal-component ref="personal-goal-add-task-modal">
        </design-2021-personal-goal-widget-add-task-modal-component>

        <design-2021-personal-goal-widget-add-modal-component
            ref="personal-goal-add-modal"
            @add-task="addPersonalGoalTask"></design-2021-personal-goal-widget-add-modal-component>
    </div>
</template>

<script>
import { ords } from '../../../app';

const personalGoalsOrds = ords.use('personalGoal');

export default {
    name: 'design-2021-personal-goal-widget-archived-component',
    props: {
        PersonalGoalsIn: Array,
        teacherAccess: Number,
    },
    data() {
        return {
            personalGoals: [],
        };
    },
    mounted() {
        personalGoalsOrds.add(this.PersonalGoalsIn);

        personalGoalsOrds
            .with('tasks', (taskCallback) => {
                taskCallback.with('personal_goal_task_deadline', (deadlineCallback) => {
                    deadlineCallback.with('agenda_item');
                });
            })
            .where('archived', '=', 1)
            .get()
            .subscribe((personalGoals) => {
                this.personalGoals = personalGoals;
            });
    },
    methods: {
        showPersonalGoals() {
            window.location.href = this.routes.personalGoals();
        },

        unarchivePersonalGoal(goal) {
            this.alert.saving();

            this.axios.post(this.routes.personalGoalUnArchive(goal.id)).then(
                (response) => {
                    goal.update({ archived: 0 });

                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        showUpdatePersonalGoalModal(goal) {
            this.$refs['personal-goal-update-modal'].show(goal);
        },

        removePersonalGoal(goal) {
            this.alert
                .confirm(this.$t('common.delete_learning_goal'), this.$t('personal_goals.confirm_delete_goal'))
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }
                    this.alert.saving();
                    this.axios.delete(this.routes.personalGoalRemove(goal.id)).then(
                        (response) => {
                            goal.remove();
                            this.alert.success();
                        },
                        (error) => {
                            return this.alert.ajaxError(error);
                        },
                    );
                });
        },

        addPersonalGoal() {
            this.$refs['personal-goal-add-modal'].show();
        },

        addPersonalGoalTask(goal) {
            this.$refs['personal-goal-add-task-modal'].show(goal);
        },

        updateTaskCheckedState(goalId, task, checked) {
            const data = {
                checked: checked,
            };

            this.axios.post(this.routes.personalGoalTaskStatusUpdate(task.id), data).then(
                (response) => {
                    task.update(response.data);
                },
                (error) => {
                    return this.alert.ajaxError(error);
                },
            );
        },

        getPersonalGoalTasksStatus(goal) {
            let status = '';
            const stats = goal.getPersonalGoalTasksStatuses();

            if (stats.finished === 0 && stats.unfinished === 0) {
                status = this.$t('personal_goals.this_learning_goal_has_no_tasks');
            } else if (stats.finished === goal.tasks.length) {
                status = this.$t('personal_goals.all_tasks_completed');
            } else if (stats.finished > 0) {
                status = `${stats.finished} van de ${goal.tasks.length} ` + this.$t('personal_goals.tasks_completed');
            } else {
                status = this.$t('personal_goals.no_tasks_completed');
            }
            return status;
        },
    },
};
</script>

<style scoped lang="scss">
.personal-goals {
    max-height: 600px;
    overflow-y: auto;
    height: max-content;
    margin-bottom: 0;

    title {
        margin-bottom: 0;
    }
}

.personal-goal-main-nav ::v-deep,
.personal-goal-nav ::v-deep {
    .dropdown-menu {
        background-color: $secondary-background-color;

        li {
            &:hover,
            &:focus {
                background-color: $info;
            }

            .dropdown-item {
                &:hover,
                &:focus {
                    background-color: $info;
                }
            }
        }
    }
}

.personal-goal-nav ::v-deep {
    margin-top: map-get($grid-gutter-spacing, 'eight');

    .btn-add-task,
    .btn-edit-personal-goal,
    .btn-archive-personal-goal {
        i {
            color: $primary;
            font-style: normal;
        }
    }

    .btn-add-task,
    .btn-edit-personal-goal,
    .btn-remove-personal-goal,
    .btn-archive-personal-goal {
        .dropdown-item {
            display: flex;
            flex-direction: row;
            align-items: center;

            i {
                margin-right: map-get($grid-gutter-spacing, 'eight');
                width: 20px;

                &:before {
                    margin-right: 0;
                }
            }
        }
    }
}

.personal-goal {
    margin-left: -#{map-get($grid-gutter-spacing, 'twenty-one')};
    margin-right: -#{map-get($grid-gutter-spacing, 'twenty-one')};
}

.personal-goal-content {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    flex: 1;
    align-items: center;
    border-bottom: 1px solid $border-color;
    position: relative;
    padding-left: map-get($grid-gutter-spacing, 'twenty-one');
    padding-right: map-get($grid-gutter-spacing, 'twenty-one');
    border-left: 2px solid transparent;
    border-right: 2px solid transparent;

    &:not(.collapsed) {
        background-color: $secondary-background-color;
    }

    &,
    &:hover {
        cursor: pointer;
    }

    &:hover {
        background-color: $secondary-background-color;
        border-color: $white;
        border-radius: $border-radius-content;
    }

    .personal-goal-title {
        flex: 1;
        font-weight: $font-weight-medium;
        font-size: $base-font-size;
    }
}

.task-list .task {
    padding: map-get($grid-gutter-spacing, 'ten') map-get($grid-gutter-spacing, 'fifteen')
        map-get($grid-gutter-spacing, 'ten') (map-get($grid-gutter-spacing, 'twenty-one') * 2);
    border-bottom: 1px solid $border-color;
}

.task-list-empty {
    padding: map-get($grid-gutter-spacing, 'fifteen');
    border-bottom: 1px solid $border-color;

    .alert {
        margin: 0;

        &:hover {
            cursor: pointer;
        }
    }
}

.add-task {
    display: flex;
    justify-content: flex-end;
    margin-top: map-get($grid-gutter-spacing, 'twenty-one');
    margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
    padding-right: map-get($grid-gutter-spacing, 'twenty-one');
}

.no-personal-goal-found {
    padding: map-get($grid-gutter-spacing, 'fifteen');

    .alert {
        margin-bottom: 0;
    }
}

.show-more {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: map-get($grid-gutter-spacing, 'twenty-one');

    .btn-show-more-modules {
        font-weight: $font-weight-bold;
    }
}

@media (min-width: map-get($grid-breakpoints, 'lg')) and (max-width: map-get($grid-breakpoints, 'xl')) {
    .page-wrapper:not(.navbar-toggled) {
        .personal-goal-content {
            .goals-chart ::v-deep {
                display: flex;
                flex: 1 0 100%;
                justify-content: flex-end;
            }

            .personal-goal-title {
                margin-left: 0;
            }

            .personal-goal-nav {
                margin-top: 0;
                position: absolute;
                right: 0;
            }
        }
    }
}

@-moz-document url-prefix() {
    .personal-goal-content {
        .personal-goal-title {
            margin-left: -15px !important;
        }
    }
}
</style>
